import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Box, LinearProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useCommonTranslation } from '../../../hooks/useCommonTranslation';

import { TripLegType } from '../../../data/models/reservation.model';

import { phoneUtils } from '../../../services/phoneUtils';
import reservationUtils from '../../../services/reservationUtils';

import * as clientSlice from '../../../data/client.slice';
import * as passengerSlice from '../../../data/passenger.slice';
import * as tripLegSlice from '../../../data/tripleg.slice';
import * as driverSlice from '../../../data/driver.slice';
import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import { getSmsAll } from '../../../data/messages.slice';

import TripLegDialog from './TripLegDialog';
import ReservationDetailsHeader from './ReservationDetailsHeader';
import ReservationDetails from './ReservationDetails';

const PREFIX = 'ReservationDetailsPage';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`&.${classes.container}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
  },
}));

const ReservationDetailsPage = () => {
  const dispatch = useAppDispatch();
  const t = useCommonTranslation();

  const { passengerId, referenceId } = useParams<{
    passengerId: string;
    referenceId: string;
  }>();

  const passenger = useAppSelector(passengerSlice.selectCurrentPassenger);
  const loadingPassenger = useAppSelector(
    passengerSlice.selectLoadingCurrentPassenger,
  );
  const pastReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerPastReservations,
  );
  const futureReservations = useAppSelector(
    passengerSlice.selectCurrentPassengerFutureReservations,
  );
  const clientData = useAppSelector(clientSlice.selectPassengerClientData);
  const tripLegEvent = useAppSelector(tripLegSlice.selectTripLegEventEmitted);
  const fallbackReservation = useAppSelector(
    passengerSlice.selectCurrentPassengerReservationLookup,
  );
  const loadingReservation = useAppSelector(
    passengerSlice.selectLoadingCurrentPassengerReservationLookup,
  );
  const loadedReservationLookup = useAppSelector(
    passengerSlice.selectLoadedCurrentPassengerReservationLookup,
  );

  const loadedPastReservations = useAppSelector(
    passengerSlice.selectLoadedCurrentPassengerPastReservations,
  );
  const loadedFutureReservations = useAppSelector(
    passengerSlice.selectLoadedCurrentPassengerFutureReservations,
  );

  const [legIndex, setLegIndex] = useState(0);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const reservationFromStore = useMemo(() => {
    const fromPast = pastReservations.reservations.find(
      (res) => res.reservationReferenceID === referenceId,
    );
    const fromFuture = futureReservations.reservations.find(
      (res) => res.reservationReferenceID === referenceId,
    );

    if (!fromPast && !fromFuture) return undefined;
    if (fromPast && !fromFuture) return fromPast;
    if (!fromPast && fromFuture) return fromFuture;

    // Merge trip legs from both lists to include all legs (e.g. active + canceled)
    const futureLegIDs = new Set(
      fromFuture!.tripLegs.map((l) => l.tripLegReferenceID),
    );
    const pastOnlyLegs = fromPast!.tripLegs.filter(
      (l) => !futureLegIDs.has(l.tripLegReferenceID),
    );
    const mergedLegs = [...pastOnlyLegs, ...fromFuture!.tripLegs].sort(
      (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
    );
    return { ...fromFuture!, tripLegs: mergedLegs };
  }, [pastReservations, futureReservations, referenceId]);

  const reservation = useMemo(() => {
    const base = fallbackReservation ?? reservationFromStore;
    if (!base) return undefined;

    const other = fallbackReservation ? reservationFromStore : undefined;
    if (!other) return base;

    const baseIds = new Set(base.tripLegs.map((l) => l.tripLegReferenceID));
    const extraLegs = other.tripLegs.filter(
      (l) => !baseIds.has(l.tripLegReferenceID),
    );
    if (extraLegs.length === 0) return base;

    const mergedLegs = [...extraLegs, ...base.tripLegs].sort(
      (a, b) => (a.tripLegSequence || 0) - (b.tripLegSequence || 0),
    );
    return { ...base, tripLegs: mergedLegs };
  }, [fallbackReservation, reservationFromStore]);

  const tripLeg = useMemo(
    () => reservation?.tripLegs[legIndex],
    [reservation, legIndex],
  );

  const isRoundTrip = useMemo(
    () => (reservation ? reservationUtils.isRoundTrip(reservation) : false),
    [reservation],
  );

  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);

  const provider = useAppSelector((state) =>
    tripLeg?.transportationProviderID
      ? providerSlice.selectProviderByID(
          state,
          tripLeg.transportationProviderID,
        )
      : undefined,
  );

  const [searchParams, setSearchParams] = useSearchParams();

  const handleEdit = () => {
    setIsEditDialogOpen(true);
  };

  useEffect(() => {
    const tripLegID = searchParams.get('tripLegID');
    if (!tripLegID) return;

    const idx = reservation?.tripLegs?.findIndex(
      (tl) => tl.tripLegReferenceID === tripLegID,
    );
    if (idx !== undefined && idx >= 0) {
      setLegIndex(idx);
    } else if (
      passengerId &&
      referenceId &&
      passenger?.passengerUUID &&
      fallbackReservation !== null &&
      fallbackReservation !== undefined
    ) {
      dispatch(
        passengerSlice.fetchMissingTripLegIntoReservationLookupAsync(
          passenger.passengerUUID,
          referenceId,
          tripLegID,
        ),
      );
    }
  }, [
    searchParams,
    reservation,
    fallbackReservation,
    passenger,
    passengerId,
    referenceId,
    dispatch,
  ]);

  useEffect(() => {
    const intervalFn = () => {
      if (tripLeg && syncedTripLegs) {
        const syncedTripLeg = syncedTripLegs[tripLeg.tripLegReferenceID!];
        const driverId = syncedTripLeg?.driverID;
        const isTripLegInProgress =
          reservationUtils.isTripLegInProgress(tripLeg);

        if (driverId && isTripLegInProgress) {
          dispatch(driverSlice.fetchDriverLocationAsync(driverId));
        }
      }
    };
    intervalFn();
    const intervalId = setInterval(intervalFn, 10000);

    return () => clearInterval(intervalId);
  }, [tripLeg, syncedTripLegs, dispatch]);

  useEffect(() => {
    dispatch(tripLegSlice.clearTripLegDetails());
    dispatch(accountingSlice.fetchTransportationAttributesAsync());
  }, []);

  useEffect(() => {
    reservation?.tripLegs.forEach((tl) => {
      dispatch(tripLegSlice.getTripLegDetailsAsync(tl.tripLegReferenceID!));
    });
    if (
      reservation?.tripLegs[legIndex]?.transportationProviderID &&
      provider?.transportationProviderID !==
        reservation.tripLegs[legIndex]?.transportationProviderID
    ) {
      dispatch(
        providerSlice.fetchProviderAsync(
          reservation.tripLegs[legIndex]?.transportationProviderID!,
        ),
      );
    }
  }, [reservation]);

  useEffect(() => {
    const passengerMobilePhone = phoneUtils.getPassengerMobilePhone(
      passenger?.passengerPhones || [],
    );
    const phoneNumber = phoneUtils.getNumbersOnly(
      passengerMobilePhone?.phoneNumber || '',
    );
    if (phoneNumber) {
      const initialState = {
        phoneNumber,
        page: 1,
        pageSize: 5,
      };
      dispatch(getSmsAll(initialState));
    }
  }, [dispatch, passenger]);

  useEffect(() => {
    if (passengerId) {
      dispatch(passengerSlice.fetchPassengerAsync(passengerId));
    }
  }, [dispatch, passengerId]);

  useEffect(() => {
    if (passengerId && !loadedPastReservations) {
      dispatch(
        passengerSlice.fetchPassengerPastReservationsAsync(passengerId, 100),
      );
    }
  }, [dispatch, passengerId, loadedPastReservations]);

  useEffect(() => {
    if (passengerId && !loadedFutureReservations) {
      dispatch(
        passengerSlice.fetchPassengerFutureReservationsAsync(passengerId, 100),
      );
    }
  }, [dispatch, passengerId, loadedFutureReservations]);

  useEffect(() => {
    dispatch(passengerSlice.clearCurrentPassengerReservationLookup());
  }, [dispatch, passengerId, referenceId]);

  useEffect(() => {
    if (
      !passengerId ||
      !referenceId ||
      loadingReservation ||
      loadedReservationLookup
    ) {
      return;
    }
    dispatch(
      passengerSlice.fetchPassengerReservationByReferenceAsync(
        passengerId,
        referenceId,
        t('common.reservationDetailsLoadError'),
      ),
    );
  }, [
    dispatch,
    passengerId,
    referenceId,
    loadingReservation,
    loadedReservationLookup,
    t,
  ]);

  useEffect(() => {
    if (passenger) {
      dispatch(
        clientSlice.fetchPassengerClientDataAsync(
          passenger.clientBC,
          passenger.servicePlans,
        ),
      );
    }
  }, [passenger]);

  useEffect(() => {
    if (tripLegEvent) {
      const updatedLegId = tripLegEvent.detail.tripLegReferenceID;
      const matchesCurrent = reservation?.tripLegs.some(
        (tl) => tl.tripLegReferenceID === updatedLegId,
      );
      if (matchesCurrent && passengerId && referenceId) {
        if (updatedLegId) {
          dispatch(tripLegSlice.getTripLegDetailsAsync(updatedLegId));
        }
        dispatch(
          passengerSlice.fetchPassengerReservationByReferenceAsync(
            passengerId,
            referenceId,
            t('common.reservationDetailsLoadError'),
          ),
        );
      }
      dispatch(tripLegSlice.setTripLegEventEmitted(null));
    }
  }, [tripLegEvent, reservation, passengerId, referenceId, dispatch, t]);

  return (
    <StyledBox className={classes.container}>
      <ReservationDetailsHeader
        passenger={passenger}
        onEdit={handleEdit}
        editDisabled={!tripLeg}
      />
      {tripLeg && (
        <ReservationDetails
          passenger={passenger}
          reservation={reservation}
          syncedTripLegs={syncedTripLegs || null}
          legIndex={legIndex}
          setLegIndex={setLegIndex}
        />
      )}
      {tripLeg && passenger && referenceId && (
        <TripLegDialog
          sequence={tripLeg.tripLegSequence}
          reservationReferenceID={referenceId}
          tripLeg={tripLeg}
          tripType={TripLegType.MultiLeg}
          open={isEditDialogOpen}
          passenger={passenger}
          servicePlans={clientData.servicePlans}
          close={() => setIsEditDialogOpen(false)}
        />
      )}
    </StyledBox>
  );
};

export default ReservationDetailsPage;
